<template lang="pug">
div.pt-3.pb-3.comment-verses(
  :class="{ 'comment-verses--expanded': expanded, 'comment-verses--collapsed': !expanded }"
)
  div.d-flex.align-start
    div.comment-verses__left
      VSkeletonLoader(
        v-if="humanReadableRangeQuery.isPending.value"
        width="100"
        type="text"
      )
      ErrorAlert(
        v-else-if="humanReadableRangeError"
        :stack="humanReadableRangeError.stack"
        :message="i18n.t('BibleRef.FailedToRenderReference')"
        @retry="humanReadableRangeQuery.refetch"
        @report="() => report(humanReadableRangeError)"
      )
      div.text-caption.mb-2(
        v-else
        v-html="humanReadableRange ?? '…'"
      )

      VSkeletonLoader(
        v-if="allVersesQuery.isPending.value"
        type="paragraph"
      )
      ErrorAlert(
        v-else-if="allVersesError"
        :stack="allVersesError.stack"
        :message="i18n.t('BibleRef.FailedToLoadVerses')"
        @retry="allVersesQuery.refetch"
        @report="() => report(allVersesError)"
      )
      div.comment-verses__verses(v-else)
        BibleVerse(
          v-for="(v, i) in verses"
          :key="v.fromVid"
          :verse="v"
          :first="i === 0"
          :selected-word-ordinals="vidRange.wordOrdinals"
          :show-heading="true"
          show-book-and-chapter="never"
        )

    div.comment-verses__right
      VBtn.ma-2.d-print-none(
        :title="expanded ? i18n.t('BibleRef.Collapse') : i18n.t('BibleRef.Expand')"
        :aria-label="expanded ? i18n.t('BibleRef.Collapse') : i18n.t('BibleRef.Expand')"
        size="x-small"
        variant="outlined"
        height="40px"
        @click="expanded = !expanded"
      )
        VIcon {{ expanded ? mdiMinus : mdiPlus }}

  div.d-flex
    VSpacer
    VBtn.ma-2.d-print-none(
      v-if="expanded"
      :to="localePath(bibleLocation)"
      @click="onClickLinkCanGoOutsideModal"
    )
      VIcon.mr-1 {{ mdiEye }}
      | {{ i18n.t("BibleRef.ReadInBible") }}
</template>

<script setup lang="ts">
import type { Verse, VidRange } from "~/units/scripture";
import {
  createVerse,
  dumpRange,
  getRangeVerses,
  limitRangeAfter,
  renderRangeReference,
  withTranslation,
} from "~/units/scripture";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { mdiEye, mdiMinus, mdiPlus } from "@mdi/js";
import { useQuery } from "@tanstack/vue-query";
import { useModalRouterUtils } from "~/composables/useModalRouterUtils";
import { injectRequired } from "~/injectRequired";
import { getBibleLocationFromRange } from "~/routing/locations";
import { LogFunctionKey, ScriptureClientKey } from "~/injectionKeys";
import { makeResultAsyncSerializable } from "~/errors/util";
import { useReport } from "~/logging/report";

const report = useReport();

const scriptureClient = injectRequired(ScriptureClientKey);
const log = injectRequired(LogFunctionKey);
const i18n = useI18n();

const localePath = useLocalePath();

interface Props {
  vidRange: VidRange;

  /**
   * The ID of the user's preferred local translation.
   */
  localTid: string;
}
const { onClickLinkCanGoOutsideModal } = useModalRouterUtils();

const props = withDefaults(defineProps<Props>(), {});

const expanded = ref<boolean>(false);

const bibleLocation = computed(() => getBibleLocationFromRange(props.vidRange));

/**
 * The rendered verse range.
 */
const humanReadableRangeQuery = useQuery({
  queryKey: [
    "CommentVerses",
    "renderReference",
    computed(() => dumpRange(props.vidRange)),
    computed(() => props.localTid),
  ],
  queryFn: () =>
    makeResultAsyncSerializable(
      () =>
        renderRangeReference(
          props.vidRange,
          scriptureClient,
          props.localTid,
          true,
          true,
        ),
      log,
    ),
});

const humanReadableRange = computed<string | null>(
  () => humanReadableRangeQuery.data.value?.value ?? null,
);

const humanReadableRangeError = computed(
  () =>
    humanReadableRangeQuery.error.value ??
    humanReadableRangeQuery.data.value?.error,
);

/**
 * The verses that are referenced in the `vidRange` prop.
 */
const allVersesQuery = useQuery({
  queryKey: [
    "CommentVerses",
    computed(() => dumpRange(props.vidRange)),
    computed(() => props.localTid),
  ],
  queryFn: () =>
    makeResultAsyncSerializable(() => {
      return limitRangeAfter(
        withTranslation(props.vidRange, props.vidRange.tid || props.localTid),
        scriptureClient,
        50,
      ).andThen((range) => getRangeVerses(range, scriptureClient));
    }, log),
});

const allVerses = computed<Verse[]>(() =>
  (allVersesQuery.data.value?.value || []).map((v) => createVerse(v)),
);

const allVersesError = computed(
  () => allVersesQuery.error.value ?? allVersesQuery.data.value?.error,
);

/**
 * The verses to display.
 */
const verses = computed<Verse[]>(() => {
  const v = allVerses.value;
  if (!v.length) {
    return [];
  }

  if (!expanded.value && v[0]) {
    return [v[0]];
  }

  return v;
});

onServerPrefetch(async () => {
  await humanReadableRangeQuery.suspense();
  await allVersesQuery.suspense();
});
</script>

<style lang="scss">
.comment-verses {
  .verse__content {
    margin-bottom: 0;
  }

  &--collapsed {
    .comment-verses__left {
      overflow: hidden;
    }

    .verse__content {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
.comment-verses__left {
  flex-grow: 1;
}
</style>
